import type { LineupEntry, LineupPlayer, LineupsFile, LineupSeason } from '../../shared/types';

/**
 * Coaching and luck metrics, derived from the build-time lineup ledger.
 *
 * Imports no React on purpose: everything here is reachable from the esbuild
 * harness, which compiles this module and asserts against the frozen 2025
 * fixtures. Putting the maths in the view would make it testable only through
 * a browser.
 *
 * Everything is keyed on **user_id**. Roster ids only mean something inside a
 * single season's league.
 *
 * A NOTE ON WHAT THE PROJECTIONS ARE. They are Rotowire's generic-PPR numbers
 * as served by Sleeper, not this league's exact scoring (which is full PPR with
 * four yardage bonuses). They are genuinely pre-game -- verified by correlation
 * against actuals, r = 0.683 on 2025 week 5, where hindsight-contaminated
 * numbers would sit near 0.99. Every metric below compares players WITHIN a
 * position, where the scoring difference is close to a constant offset and so
 * barely moves the ordering.
 */

// --- Slot eligibility ------------------------------------------------------

/**
 * Which positions may fill which lineup slot.
 *
 * Only the slots this league has actually used are listed; anything unknown is
 * treated as accepting nothing, so a new slot type shows up as an unfillable
 * slot rather than silently accepting every player.
 */
const SLOT_ELIGIBILITY: Record<string, readonly string[]> = {
  QB: ['QB'],
  RB: ['RB'],
  WR: ['WR'],
  TE: ['TE'],
  K: ['K'],
  DEF: ['DEF'],
  FLEX: ['RB', 'WR', 'TE'],
  REC_FLEX: ['WR', 'TE'],
  WRRB_FLEX: ['RB', 'WR'],
  SUPER_FLEX: ['QB', 'RB', 'WR', 'TE'],
};

/** The non-bench entries of `roster_positions`, which is what `starters` aligns to. */
export function startingSlots(rosterPositions: string[]): string[] {
  return rosterPositions.filter((p) => p !== 'BN' && p !== 'IR' && p !== 'TAXI');
}

function eligible(slot: string, position: string | null): boolean {
  if (!position) return false;
  return (SLOT_ELIGIBILITY[slot] ?? []).includes(position);
}

/**
 * Best lineup obtainable from `pool`, scoring each player by `value`.
 *
 * Fills the most restrictive slots first, taking the best eligible player for
 * each. That greedy order is optimal here because slot eligibility is *nested*
 * -- every FLEX accepts a superset of what RB, WR and TE accept, and SUPER_FLEX
 * a superset of FLEX. With a chain like that, no assignment can beat taking the
 * best available at each step from narrowest to widest. (It would NOT be
 * optimal for overlapping-but-incomparable slots, e.g. a WR/TE flex alongside a
 * RB/WR flex, so the assumption is asserted rather than assumed: see the
 * harness, which reproduces Sleeper's own potential-points figure.)
 *
 * Returns the chosen players plus the total under `value`. Players the `value`
 * function cannot rank (a null projection) are placed last and only used to
 * fill a slot that would otherwise sit empty -- a manager reading the
 * projections page sees a blank there and has nothing to act on.
 */
export function bestLineup(
  slots: string[],
  pool: PlayedPlayer[],
  value: (p: PlayedPlayer) => number | null,
): { chosen: PlayedPlayer[]; byValue: number; byActual: number } {
  const order = [...slots].sort(
    (a, b) => (SLOT_ELIGIBILITY[a]?.length ?? 0) - (SLOT_ELIGIBILITY[b]?.length ?? 0),
  );

  const used = new Set<LineupPlayer>();
  const chosen: PlayedPlayer[] = [];

  for (const slot of order) {
    let pick: PlayedPlayer | null = null;
    let pickValue = -Infinity;
    let pickRankable = false;

    for (const p of pool) {
      if (used.has(p) || !eligible(slot, p[1])) continue;
      const v = value(p);
      const rankable = v !== null;
      // A rankable player always beats an unrankable one, regardless of score.
      if (pick && pickRankable && !rankable) continue;
      if (pick && !pickRankable && rankable) {
        pick = p;
        pickValue = v;
        pickRankable = true;
        continue;
      }
      const score = v ?? -Infinity;
      if (!pick || score > pickValue) {
        pick = p;
        pickValue = score;
        pickRankable = rankable;
      }
    }

    if (pick) {
      used.add(pick);
      chosen.push(pick);
    }
  }

  const round2 = (n: number) => Math.round(n * 100) / 100;
  return {
    chosen,
    byValue: round2(chosen.reduce((t, p) => t + (value(p) ?? 0), 0)),
    byActual: round2(chosen.reduce((t, p) => t + p[3], 0)),
  };
}

// --- Per-manager-week analysis ---------------------------------------------

export interface WeekAnalysis {
  season: string;
  week: number;
  userId: string;
  matchupId: number;
  isPlayoff: boolean;

  /** What the lineup they actually started scored. */
  actual: number;
  /** Sum of projections for the lineup they started. Drives expected wins. */
  projected: number;
  /**
   * What a manager who blindly followed the projections would have scored, in
   * REAL points. The counterfactual the coaching metric is measured against.
   */
  chalk: number;
  /** The hindsight ceiling: the best the roster could possibly have done. */
  perfect: number;

  /**
   * `chalk - actual`. Positive means following the projections would have
   * beaten what they did -- a foreseeable cost. Negative means they beat the
   * chalk, which is the only thing here that can be called a good call.
   */
  coachingCost: number;
  /**
   * `perfect - chalk`. Points nobody could have captured without hindsight,
   * because the projections pointed elsewhere.
   */
  unforeseeable: number;
  /** `perfect - actual`, and always equal to coachingCost + unforeseeable. */
  regret: number;

  /**
   * Started someone while a benched player at an eligible position was BOTH
   * projected higher AND scored higher. Unambiguous, and unlike `coachingCost`
   * it needs no counterfactual lineup to explain.
   */
  clearMistakes: number;

  /** The costliest of those, for pages that would rather name names. */
  biggestMiss: {
    started: LineupPlayer;
    benched: LineupPlayer;
    /** Points gained by making the swap. Always positive. */
    swing: number;
  } | null;
}

const round2 = (n: number) => Math.round(n * 100) / 100;

/**
 * A lineup every one of whose players has finished playing.
 *
 * The whole page is counterfactual -- perfect lineup, chalk lineup, the regret
 * between them -- and every one of those compares a real score against a score
 * some other lineup WOULD have had. A player who has not kicked off contributes
 * 0 to the counterfactual and 0 to the actual, so a half-played week does not
 * degrade gracefully: it reports a manager as having left nothing on the bench,
 * because nobody on the bench has played either. Narrowing the type is what
 * makes that unrepresentable rather than merely discouraged.
 */
type PlayedPlayer = [
  playerId: string,
  position: string | null,
  projected: number | null,
  actual: number,
];

interface PlayedEntry {
  m: number;
  s: PlayedPlayer[];
  b: PlayedPlayer[];
}

const hasPlayed = (p: LineupPlayer): p is PlayedPlayer => p[3] !== null;

/** The entry, or null if any of its players is still to play. */
function settledEntry(entry: LineupEntry): PlayedEntry | null {
  const s = entry.s.filter(hasPlayed);
  const b = entry.b.filter(hasPlayed);
  if (s.length !== entry.s.length || b.length !== entry.b.length) return null;
  return { m: entry.m, s, b };
}

function analyseEntry(
  season: string,
  week: number,
  userId: string,
  entry: PlayedEntry,
  slots: string[],
  playoffWeekStart: number | null,
): WeekAnalysis {
  const pool = [...entry.s, ...entry.b];

  const actual = round2(entry.s.reduce((t, p) => t + p[3], 0));
  const projected = round2(entry.s.reduce((t, p) => t + (p[2] ?? 0), 0));

  const chalk = bestLineup(slots, pool, (p) => p[2]).byActual;
  const perfect = bestLineup(slots, pool, (p) => p[3]).byActual;

  // Count only mistakes that were visible at the time AND cost real points.
  // A benched player who outscored a starter but was projected lower is
  // unlucky, not misplayed; that distinction is the whole point of the page.
  let clearMistakes = 0;
  let biggestMiss: WeekAnalysis['biggestMiss'] = null;
  const claimed = new Set<PlayedPlayer>();
  for (const started of entry.s) {
    let best: PlayedPlayer | null = null;
    for (const benched of entry.b) {
      if (claimed.has(benched)) continue;
      if (benched[1] !== started[1]) continue; // same position: always swappable
      if (benched[2] === null || started[2] === null) continue;
      if (benched[2] <= started[2] || benched[3] <= started[3]) continue;
      if (!best || benched[3] > best[3]) best = benched;
    }
    if (best) {
      claimed.add(best);
      clearMistakes += 1;
      const swing = round2(best[3] - started[3]);
      if (!biggestMiss || swing > biggestMiss.swing) {
        biggestMiss = { started, benched: best, swing };
      }
    }
  }

  const coachingCost = round2(chalk - actual);
  const unforeseeable = round2(perfect - chalk);

  return {
    season,
    week,
    userId,
    matchupId: entry.m,
    isPlayoff: playoffWeekStart !== null && week >= playoffWeekStart,
    actual,
    projected,
    chalk,
    perfect,
    coachingCost,
    unforeseeable,
    regret: round2(perfect - actual),
    clearMistakes,
    biggestMiss,
  };
}

/** Every manager-week in the ledger, chronologically. */
export function analyseAll(file: LineupsFile | null): WeekAnalysis[] {
  if (!file) return [];
  const out: WeekAnalysis[] = [];

  for (const season of Object.values(file.seasons) as LineupSeason[]) {
    const slots = startingSlots(season.rosterPositions);
    for (const [weekKey, managers] of Object.entries(season.weeks)) {
      const week = Number(weekKey);
      // The week in progress is in the ledger for the trade tab's benefit, and
      // is not a coaching measurement. Skipped as a WEEK rather than per
      // manager, because `pairGames` needs both halves of a game or it drops
      // one side and every league-wide total goes odd.
      if (season.pendingWeek === week) continue;
      for (const [userId, entry] of Object.entries(managers)) {
        const settled = settledEntry(entry);
        if (!settled) continue;
        out.push(
          analyseEntry(season.season, week, userId, settled, slots, season.playoffWeekStart),
        );
      }
    }
  }

  return out.sort(
    (a, b) => Number(a.season) - Number(b.season) || a.week - b.week || a.matchupId - b.matchupId,
  );
}

// --- Games -----------------------------------------------------------------

export interface AnalysedGame {
  season: string;
  week: number;
  isPlayoff: boolean;
  a: WeekAnalysis;
  b: WeekAnalysis;
  /** The side projected to win. Null when the two projections are identical. */
  favouriteId: string | null;
  /** The side that actually won. Null on a tie. */
  winnerId: string | null;
}

/**
 * Pairs manager-weeks into games via `matchup_id`.
 *
 * The ledger only contains rows that carried a matchup id, so playoff byes and
 * post-elimination weeks -- where Sleeper keeps scoring lineups that are not
 * competing against anything -- never reach this function.
 */
export function pairGames(weeks: WeekAnalysis[]): AnalysedGame[] {
  const byKey = new Map<string, WeekAnalysis[]>();
  for (const w of weeks) {
    const k = `${w.season}|${w.week}|${w.matchupId}`;
    byKey.set(k, [...(byKey.get(k) ?? []), w]);
  }

  const games: AnalysedGame[] = [];
  for (const sides of byKey.values()) {
    if (sides.length !== 2) continue;
    const [a, b] = sides;
    games.push({
      season: a.season,
      week: a.week,
      isPlayoff: a.isPlayoff,
      a,
      b,
      favouriteId:
        a.projected === b.projected ? null : a.projected > b.projected ? a.userId : b.userId,
      winnerId: a.actual === b.actual ? null : a.actual > b.actual ? a.userId : b.userId,
    });
  }

  return games.sort(
    (x, y) => Number(x.season) - Number(y.season) || x.week - y.week,
  );
}

// --- Win probability -------------------------------------------------------

/**
 * How projected margins map onto real ones, fitted from the league's own games.
 *
 * The naive model -- "whoever is projected higher was expected to win" -- is
 * badly overconfident here, and the data says why. Every team's projected total
 * lands in a narrow band (118-145 per week in 2025, sd of the projected margin
 * 16.1) while actual margins swing more than twice as wide (sd 38.6). Treating a
 * two-point projected edge as a certain win produced a manager who went 6-10
 * with "1 expected win", which is nonsense dressed as a statistic.
 *
 * So: regress actual margin on projected margin, then read a win probability
 * off the residual spread. Fitted rather than hard-coded so it re-calibrates as
 * seasons accumulate. Measured on 2025: slope 1.52, residual sd 29.9, r = 0.632,
 * and the projected favourite won 59 of 81 (72.8%).
 *
 * The slope above 1 is the interesting part -- projections systematically
 * UNDERSTATE margins, because they regress every roster toward the middle.
 */
export interface MarginFit {
  games: number;
  slope: number;
  /** Residual standard deviation: the spread a projection cannot explain. */
  sigma: number;
  /** Correlation between projected and actual margin. */
  r: number;
  /** True when the fit came from enough games to be worth trusting. */
  fitted: boolean;
}

/** Below this many games a fit is noise, so fall back to a neutral prior. */
const MIN_FIT_GAMES = 30;
/** Neutral fallback: projections taken at face value, league-typical spread. */
const FALLBACK: MarginFit = { games: 0, slope: 1, sigma: 30, r: 0, fitted: false };

/**
 * Regressed through the ORIGIN, deliberately.
 *
 * A game has no canonical "first" side -- entering it as (a, b) or (b, a) is an
 * arbitrary choice that flips the sign of both margins. The relationship is
 * therefore antisymmetric by construction and the true intercept is exactly
 * zero; fitting one would be modelling an artifact of iteration order. It also
 * has a concrete consequence: only with a zero intercept do the two sides' win
 * probabilities sum to exactly 1, which is what lets expected wins across the
 * league total the number of games played (asserted in the harness).
 */
export function fitMargins(games: AnalysedGame[]): MarginFit {
  const rows = games.map((g) => ({
    projected: g.a.projected - g.b.projected,
    actual: g.a.actual - g.b.actual,
  }));
  const n = rows.length;
  if (n < MIN_FIT_GAMES) return { ...FALLBACK, games: n };

  let sxy = 0;
  let sxx = 0;
  let syy = 0;
  for (const r of rows) {
    sxy += r.projected * r.actual;
    sxx += r.projected * r.projected;
    syy += r.actual * r.actual;
  }
  if (sxx === 0 || syy === 0) return { ...FALLBACK, games: n };

  const slope = sxy / sxx;
  let ss = 0;
  for (const r of rows) {
    const e = r.actual - slope * r.projected;
    ss += e * e;
  }

  return {
    games: n,
    slope: Math.round(slope * 1000) / 1000,
    sigma: round2(Math.sqrt(ss / n)),
    r: Math.round((sxy / Math.sqrt(sxx * syy)) * 1000) / 1000,
    fitted: true,
  };
}

/** Standard normal CDF (Abramowitz & Stegun 7.1.26). Max error ~1.5e-7. */
function normalCdf(z: number): number {
  const sign = z < 0 ? -1 : 1;
  const x = Math.abs(z) / Math.SQRT2;
  const t = 1 / (1 + 0.3275911 * x);
  const y =
    1 -
    ((((1.061405429 * t - 1.453152027) * t + 1.421413741) * t - 0.284496736) * t + 0.254829592) *
      t *
      Math.exp(-x * x);
  return 0.5 * (1 + sign * y);
}

/** Probability the side with this projected margin wins, under `fit`. */
export function winProbability(projectedMargin: number, fit: MarginFit): number {
  if (fit.sigma <= 0) return projectedMargin > 0 ? 1 : projectedMargin < 0 ? 0 : 0.5;
  return normalCdf((fit.slope * projectedMargin) / fit.sigma);
}

// --- Manager aggregates ----------------------------------------------------

export interface CoachingRow {
  userId: string;
  weeks: number;
  /** Mean points/week left on the bench that the projections had flagged. */
  coachingCostPerWeek: number;
  /** Mean points/week left on the bench that nothing could have predicted. */
  unforeseeablePerWeek: number;
  regretPerWeek: number;
  /** Share of total regret that was actually foreseeable, 0..1. */
  foreseeableShare: number;
  clearMistakes: number;
  /** Weeks where they beat the chalk. */
  beatChalk: number;
  totalActual: number;
  totalChalk: number;
}

export function coachingTable(weeks: WeekAnalysis[]): CoachingRow[] {
  const byUser = new Map<string, WeekAnalysis[]>();
  for (const w of weeks) byUser.set(w.userId, [...(byUser.get(w.userId) ?? []), w]);

  const rows: CoachingRow[] = [];
  for (const [userId, ws] of byUser) {
    const n = ws.length;
    if (n === 0) continue;
    const sum = (f: (w: WeekAnalysis) => number) => ws.reduce((t, w) => t + f(w), 0);

    const cost = sum((w) => w.coachingCost);
    const unforeseeable = sum((w) => w.unforeseeable);
    const regret = sum((w) => w.regret);

    rows.push({
      userId,
      weeks: n,
      coachingCostPerWeek: round2(cost / n),
      unforeseeablePerWeek: round2(unforeseeable / n),
      regretPerWeek: round2(regret / n),
      // Regret is non-negative but its foreseeable half can be negative when a
      // manager beat the chalk, so this ratio is deliberately allowed below 0.
      foreseeableShare: regret === 0 ? 0 : round2((cost / regret) * 100) / 100,
      clearMistakes: sum((w) => w.clearMistakes),
      beatChalk: ws.filter((w) => w.coachingCost < 0).length,
      totalActual: round2(sum((w) => w.actual)),
      totalChalk: round2(sum((w) => w.chalk)),
    });
  }

  // Lowest foreseeable cost first -- the best coaches.
  return rows.sort((a, b) => a.coachingCostPerWeek - b.coachingCostPerWeek);
}

export interface LuckRow {
  userId: string;
  games: number;
  wins: number;
  losses: number;
  ties: number;
  /** Sum of per-game win probabilities under the fitted margin model. */
  expectedWins: number;
  /** `wins - expectedWins`. Positive means they beat what the projections implied. */
  winsAboveExpectation: number;
  /** Games in which they were the projected favourite, however slightly. */
  favouredIn: number;
  /** Record against every other manager's score that week, not just their opponent. */
  allPlayWins: number;
  allPlayGames: number;
  /**
   * `wins - allPlayWinRate * games`. Isolates SCHEDULE luck specifically: who
   * you happened to be drawn against, independent of any projection.
   */
  scheduleLuck: number;
  /**
   * How much a roster beat its own projected total each week, RELATIVE TO THE
   * LEAGUE AVERAGE.
   *
   * The raw figure is not worth showing. Projections are conditional on every
   * starter playing a normal game, so actual totals land below projected for
   * almost everyone -- measured across 2025, nine of ten managers were
   * negative, averaging -5.9 per week. Printed raw that reads as "the whole
   * league is bad at this" when it is really one constant applied to everybody.
   * Centring on the league mean removes the constant and leaves the only part
   * that distinguishes managers, which is the question the column is asked to
   * answer.
   */
  projectionEdgePerWeek: number;
}

export function luckTable(
  games: AnalysedGame[],
  weeks: WeekAnalysis[],
  fit: MarginFit = fitMargins(games),
): LuckRow[] {
  const stat = new Map<string, LuckRow>();
  const blank = (userId: string): LuckRow => ({
    userId,
    games: 0,
    wins: 0,
    losses: 0,
    ties: 0,
    expectedWins: 0,
    winsAboveExpectation: 0,
    favouredIn: 0,
    allPlayWins: 0,
    allPlayGames: 0,
    scheduleLuck: 0,
    projectionEdgePerWeek: 0,
  });
  const get = (id: string) => {
    const r = stat.get(id) ?? blank(id);
    stat.set(id, r);
    return r;
  };

  for (const g of games) {
    for (const [side, foe] of [
      [g.a, g.b],
      [g.b, g.a],
    ] as const) {
      const r = get(side.userId);
      r.games += 1;
      if (g.winnerId === null) r.ties += 1;
      else if (g.winnerId === side.userId) r.wins += 1;
      else r.losses += 1;
      if (g.favouriteId === side.userId) r.favouredIn += 1;
      // The two sides' probabilities are complements by construction, so these
      // sum to exactly one game -- which the harness asserts.
      r.expectedWins += winProbability(side.projected - foe.projected, fit);
    }
  }

  // All-play: every manager's score against every OTHER score in the same week.
  const byWeek = new Map<string, WeekAnalysis[]>();
  for (const w of weeks) {
    const k = `${w.season}|${w.week}`;
    byWeek.set(k, [...(byWeek.get(k) ?? []), w]);
  }
  for (const cohort of byWeek.values()) {
    for (const me of cohort) {
      const r = get(me.userId);
      for (const other of cohort) {
        if (other === me) continue;
        r.allPlayGames += 1;
        if (me.actual > other.actual) r.allPlayWins += 1;
        else if (me.actual === other.actual) r.allPlayWins += 0.5;
      }
    }
  }

  const edge = new Map<string, { total: number; n: number }>();
  for (const w of weeks) {
    const e = edge.get(w.userId) ?? { total: 0, n: 0 };
    e.total += w.actual - w.projected;
    e.n += 1;
    edge.set(w.userId, e);
  }

  // League-wide mean edge, weighted by weeks played, so the centring below is
  // not thrown off by a manager who played fewer games.
  const totalEdge = [...edge.values()].reduce((t, e) => t + e.total, 0);
  const totalWeeks = [...edge.values()].reduce((t, e) => t + e.n, 0);
  const leagueMeanEdge = totalWeeks === 0 ? 0 : totalEdge / totalWeeks;

  for (const r of stat.values()) {
    r.expectedWins = round2(r.expectedWins);
    r.winsAboveExpectation = round2(r.wins - r.expectedWins);
    const rate = r.allPlayGames === 0 ? 0 : r.allPlayWins / r.allPlayGames;
    r.scheduleLuck = round2(r.wins - rate * r.games);
    const e = edge.get(r.userId);
    r.projectionEdgePerWeek = e && e.n > 0 ? round2(e.total / e.n - leagueMeanEdge) : 0;
  }

  return [...stat.values()].sort((a, b) => b.winsAboveExpectation - a.winsAboveExpectation);
}
